import is from '@sindresorhus/is';
import clsx from 'clsx';
import { AnimatePresence } from 'framer-motion';
import { useSnackbar } from 'notistack';
import type { FunctionComponent, ReactNode } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import Maybe from 'true-myth/maybe';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileComponent, SectionTagDefinition } from '~/shared/data-contract/job-profile';
import { createJobProfileForGenerationWithPrompt } from '~/shared/data-contract/job-profile';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import { Checkbox } from '~/ui/core/checkbox/Checkbox';
import { IconAsterisk, IconCheckmark1Small, IconChevronDownSmall } from '~/ui/core/icons';
import { RecessedSurface } from '~/ui/core/recessed-surface/RecessedSurface';
import { PromptComposer } from '~/ui/domain/PromptComposer/PromptComposer';

import type { RegenerationCommentsContext } from '../../data-contract/regeneration-comments-context';
import { useMixpanelTracking } from '../../tracking/mixpanel/use-mixpanel-tracking';
import { useRedetectSkills } from '../api/use-redetect-skills';
import { useRegenerateJobProfileDescriptionAsync } from '../api/use-regenerate-job-profile-description-async';
import { Spinner } from '../components/Spinner';
import { TranscribableTextarea } from '../components/TranscribableTextarea';
import { useJsaTrackingContext } from '../hooks/analytics/use-jsa-tracking-context';
import { CommentMessage } from './comments/CommentMessage';
import { useOptionalCommentsScope } from './comments/comments-scope-context';
import { useManualEditStore } from './ManualEditStoreProvider';
import { SwitchesWrapper } from './PopoverStyles';
import { SkillRedetectionNotification } from './SkillRedetectionNotification';
import type { ProfileForm } from './types';
import { useRegenerateCommentsAvailability } from './use-regenerate-comments-availability';
import { useSkillActionStore } from './use-skill-action-store';
import { createPendingSuggestedSkills } from './use-skill-actions';

export type RegenerationState = readonly ('CONTENT' | 'SKILLS')[];
export type RegenerationTarget = 'description' | 'skills';

export type RegenerateDialogProps = {
    readonly activeLanguage: LanguageCodeForJSA;
    readonly architectureId: string;
    readonly children: (isSubmitting: boolean) => ReactNode;
    readonly formMethods: UseFormReturn<ProfileForm>;
    readonly isOpen: boolean;
    readonly itemId?: string;
    readonly itemType?: 'job-level' | 'job-role';
    readonly onOpenChange: (open: boolean) => void;
    readonly onRegenerationStateChange?: (regenerationState: RegenerationState) => void;
    readonly onSectionTagsRegenerated?: (sectionTags: readonly SectionTagDefinition[]) => void;
    readonly parentRoleId?: string;
    readonly profile: JobProfileComponent;
};

const getCommentsOptionCardClassName = ({
    disabled,
    selected,
}: {
    readonly disabled: boolean;
    readonly selected: boolean;
}): string =>
    clsx(
        'box-border flex w-full items-center justify-between gap-m rounded-lg border-0 px-l py-xs',
        'bg-transparent shadow-none transition-[background-color,color,box-shadow,opacity] duration-200 ease-in-out',
        disabled ? 'cursor-default opacity-60' : 'cursor-pointer',
        selected ? 'text-black-900' : 'text-black-700',
    );

const getIncludeRepliesRowClassName = ({
    disabled,
    selected,
}: {
    readonly disabled: boolean;
    readonly selected: boolean;
}): string =>
    clsx(
        'box-border flex min-h-[calc(var(--spacing-5xl)+var(--spacing-xs)*2)] w-full items-center gap-s rounded-lg border-0 bg-transparent px-l py-xs shadow-none transition-[background-color,color,box-shadow,opacity] duration-200 ease-in-out',
        disabled ? 'cursor-default opacity-60' : 'cursor-pointer',
        selected ? 'text-black-900' : 'text-black-700',
    );

const getCommentsOptionLabelClassName = (selected: boolean): string =>
    clsx('truncate text-left text-sm font-medium leading-[1.25rem]', selected ? 'text-black-900' : 'text-black-700');

const getIncludeRepliesLabelClassName = (selected: boolean): string =>
    clsx('text-sm font-medium leading-[1.25rem]', selected ? 'text-black-900' : 'text-black-700');

const getCommentsBadgeVariant = (selected: boolean): 'gray' | 'orange' => (selected ? 'orange' : 'gray');

const getCommentPreviewsToggleIconClassName = (isOpen: boolean): string =>
    clsx('transition-transform duration-200', isOpen && 'rotate-180');

const commentsPreviewFadeClassName =
    'pointer-events-none absolute inset-x-0 bottom-0 h-xl bg-gradient-to-t from-white-200 to-white-200/0';

const getRegenerationTargetButtonClassName = (selected: boolean): string =>
    selected
        ? '!w-full !justify-start !text-black-900'
        : '!w-full !justify-start !bg-gray-200 hover:!bg-gray-300 !text-black-700';

const RegenerationTargetIndicator: FunctionComponent<{ readonly selected: boolean }> = ({ selected }) => (
    <span
        aria-hidden={true}
        className={
            selected
                ? 'inline-flex size-[14px] shrink-0 items-center justify-center rounded-full bg-orange-800 text-white-0 [&_svg]:fill-white-0'
                : 'inline-flex size-[14px] shrink-0 rounded-full border-[1.5px] border-gray-600'
        }
    >
        {selected ? <IconCheckmark1Small /> : null}
    </span>
);

const regenerationTargetOptions = [
    {
        ariaLabelKey: 'pages.job-architecture-v2.regenerate-dialog.apply-to-description',
        labelKey: 'pages.job-architecture-v2.regenerate-dialog.apply-to-content',
        value: 'description',
    },
    {
        ariaLabelKey: 'pages.job-architecture-v2.regenerate-dialog.apply-to-skills',
        labelKey: 'pages.job-architecture-v2.regenerate-dialog.apply-to-skills-short',
        value: 'skills',
    },
] as const satisfies readonly {
    readonly ariaLabelKey: string;
    readonly labelKey: string;
    readonly value: RegenerationTarget;
}[];

export const RegenerateDialog: FunctionComponent<RegenerateDialogProps> = ({
    activeLanguage,
    architectureId,
    children,
    formMethods,
    isOpen,
    itemId,
    itemType,
    onOpenChange,
    onRegenerationStateChange,
    onSectionTagsRegenerated,
    parentRoleId,
    profile,
}) => {
    const translate = useTranslate();
    const { enqueueSnackbar } = useSnackbar();
    const setPendingSkillSuggestions = useSkillActionStore((state) => state.setPendingSkillSuggestions);
    const resetManualEdits = useManualEditStore((state) => state.resetManualEdits);
    const { mutateAsync: regenerateJobProfileDescription } = useRegenerateJobProfileDescriptionAsync();
    const { mutateAsync: redetectSkills } = useRedetectSkills();
    const [additionalPrompt, setAdditionalPrompt] = useState('');
    const [regenerationTarget, setRegenerationTarget] = useState<RegenerationTarget>('description');
    const [includeComments, setIncludeComments] = useState(false);
    const [includeReplies, setIncludeReplies] = useState(false);
    const [areCommentPreviewsOpen, setAreCommentPreviewsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSkillRedetectionNotification, setShowSkillRedetectionNotification] = useState(false);
    const { trackEvent } = useMixpanelTracking();
    const trackingContext = useJsaTrackingContext();
    const { commentCount, comments, isAvailable: areCommentsAvailable } = useRegenerateCommentsAvailability();
    const commentsScope = useOptionalCommentsScope();

    const isActiveRef = useRef(true);
    useEffect(() => {
        isActiveRef.current = true;

        return () => {
            isActiveRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!areCommentsAvailable) {
            setIncludeComments(false);
        }
    }, [areCommentsAvailable]);

    useEffect(() => {
        if (!includeComments) {
            setIncludeReplies(false);
        }
    }, [includeComments]);

    const getTrackingIds = (): { readonly levelId?: string; readonly roleId?: string } => {
        if (itemType === 'job-role') {
            return { roleId: itemId };
        }
        if (itemType === 'job-level') {
            return { levelId: itemId, roleId: parentRoleId };
        }

        return {};
    };

    const createProfileForGenerationWithPrompt = (): JobProfileComponent &
        ReturnType<typeof createJobProfileForGenerationWithPrompt> => {
        const { skills, translations } = formMethods.getValues();

        return {
            ...profile,
            ...createJobProfileForGenerationWithPrompt({
                ...profile,
                skills,
                translations,
            }),
        };
    };

    const createCommentsContext = (): RegenerationCommentsContext | undefined => {
        if (!includeComments || is.null(commentsScope) || is.emptyArray(comments)) {
            return undefined;
        }

        return {
            ...(!is.undefined(itemId) ? { itemId } : {}),
            commentIds: comments.map((comment) => comment.id),
            includeReplies,
            language: commentsScope.language,
            version: commentsScope.version,
        };
    };

    const handleDescriptionRegeneration = async (promptMaybe: Maybe<string>): Promise<void> => {
        const jobProfileForGeneration = createProfileForGenerationWithPrompt();
        const translationsInCurrentlySelectedLanguage = jobProfileForGeneration.translations.filter(
            (translation) => translation.language === activeLanguage,
        );
        const generatedDescriptions = await regenerateJobProfileDescription({
            acceptLanguage: activeLanguage,
            additionalPrompt: promptMaybe,
            commentsContext: createCommentsContext(),
            jobProfile: { ...jobProfileForGeneration, translations: translationsInCurrentlySelectedLanguage },
        });

        if (!isActiveRef.current) {
            return;
        }

        const generatedSectionTags = generatedDescriptions.find((d) => is.nonEmptyArray(d.sectionTags))?.sectionTags;
        if (is.nonEmptyArray(generatedSectionTags)) {
            onSectionTagsRegenerated?.(generatedSectionTags);
        }

        let hasAppliedGeneratedDescription = false;

        const currentTranslations = formMethods.getValues('translations');
        const updatedTranslations = currentTranslations.map((translation) => {
            const generatedDescription = generatedDescriptions.find(
                ({ language }) => language === translation.language,
            );
            if (is.undefined(generatedDescription)) {
                return translation;
            }

            hasAppliedGeneratedDescription = true;

            return {
                ...translation,
                structured: generatedDescription.structured ?? [],
            };
        });

        if (hasAppliedGeneratedDescription) {
            formMethods.setValue('translations', updatedTranslations, { shouldDirty: true });
        }
    };

    const handleSkillsRedetection = async (promptMaybe: Maybe<string>): Promise<void> => {
        const jobProfileForGeneration = createProfileForGenerationWithPrompt();
        const skillsResult = await redetectSkills({
            additionalPrompt: promptMaybe,
            architectureId,
            commentsContext: createCommentsContext(),
            detectionType: 'redetect',
            jobProfile: jobProfileForGeneration,
            jobProfileId: profile.id,
        });

        if (!isActiveRef.current) {
            return;
        }

        const pendingSuggestions = createPendingSuggestedSkills(skillsResult);
        setPendingSkillSuggestions(pendingSuggestions);
    };

    const createPromptMaybe = (prompt: string): Maybe<string> => {
        const trimmedPrompt = prompt.trim();

        return is.nonEmptyString(trimmedPrompt) ? Maybe.just(trimmedPrompt) : Maybe.nothing<string>();
    };

    const trackRegenerationActions = (trackingIds: { readonly levelId?: string; readonly roleId?: string }): void => {
        if (regenerationTarget === 'description') {
            trackEvent({
                name: 'ai-action-used',
                payload: {
                    ...trackingContext,
                    ...trackingIds,
                    actionScope: 'section',
                    actionType: 'regenerate_description',
                    architectureId,
                    section: 'description',
                },
            });
        }

        if (regenerationTarget === 'skills') {
            trackEvent({
                name: 'ai-action-used',
                payload: {
                    ...trackingContext,
                    ...trackingIds,
                    actionScope: 'skills',
                    actionType: 'suggest_skills',
                    architectureId,
                },
            });
        }
    };

    const computeRegenerationState = (): RegenerationState => {
        const states: ('CONTENT' | 'SKILLS')[] = [];

        if (regenerationTarget === 'description') {
            states.push('CONTENT');
        }
        if (regenerationTarget === 'skills') {
            states.push('SKILLS');
        }

        return states;
    };

    const executeRegeneration = async (promptMaybe: Maybe<string>): Promise<boolean> => {
        if (regenerationTarget === 'description') {
            await handleDescriptionRegeneration(promptMaybe);
        }

        if (regenerationTarget === 'skills') {
            await handleSkillsRedetection(promptMaybe);
        }

        return regenerationTarget === 'description';
    };

    const handleSubmit = async (): Promise<void> => {
        if (isSubmitting) {
            return;
        }

        trackRegenerationActions(getTrackingIds());
        setIsSubmitting(true);
        onRegenerationStateChange?.(computeRegenerationState());

        const promptMaybe = createPromptMaybe(additionalPrompt);
        onOpenChange(false);

        try {
            const shouldShowSkillNotification = await executeRegeneration(promptMaybe);
            if (!isActiveRef.current) {
                return;
            }
            resetManualEdits();
            setShowSkillRedetectionNotification(shouldShowSkillNotification);
        } catch {
            if (!isActiveRef.current) {
                return;
            }
            onOpenChange(true);
            enqueueSnackbar(translate('pages.job-architecture-v2.an-error-occurred'), { variant: 'error' });
        } finally {
            onRegenerationStateChange?.([]);
            if (isActiveRef.current) {
                setIsSubmitting(false);
                setAdditionalPrompt('');
            }
        }
    };

    const handleRegenerationTargetChange = (value: RegenerationTarget): void => {
        setRegenerationTarget(value);
    };

    const updateIncludeComments = (shouldIncludeComments: boolean): void => {
        if (!isSubmitting) {
            setIncludeComments(shouldIncludeComments);
            setIncludeReplies(shouldIncludeComments);
        }
    };

    const toggleIncludeComments = (): void => {
        updateIncludeComments(!includeComments);
    };

    const toggleIncludeReplies = (): void => {
        if (!isSubmitting) {
            setIncludeReplies((current) => !current);
        }
    };

    const handleCancel = (): void => {
        onOpenChange(false);
    };

    const handleHideSkillRedetectionNotification = (): void => {
        setShowSkillRedetectionNotification(false);
    };

    const handleRedetectSkillsFromNotification = async (): Promise<void> => {
        await handleSkillsRedetection(Maybe.nothing<string>());
    };

    const submitLabel = (
        <Fragment>
            {isSubmitting ? <Spinner /> : <IconAsterisk className="size-[16px] text-orange-800" />}
            {translate('pages.job-architecture-v2.job-profile-regenerate')}
        </Fragment>
    );

    return (
        <Fragment>
            <PromptComposer
                align="end"
                cancelLabel={translate('common.cancel')}
                isOpen={isOpen}
                isSubmitting={isSubmitting}
                mode="popover"
                submitLabel={submitLabel}
                trigger={children(isSubmitting)}
                width="456px"
                onCancel={handleCancel}
                onSubmit={handleSubmit}
            >
                <div className="flex w-full flex-col gap-m">
                    <RecessedSurface
                        active={false}
                        hoverActive={true}
                        hoverPocketTone="neutral"
                        hoverSurfaceShadow="sm"
                        hoverSurfaceTone="white"
                        inactiveSurfaceShadow="sm"
                        inactiveSurfaceTone="white"
                        placement="outside"
                        radius="md"
                        renderMode="overlay"
                        transition="none"
                    >
                        <TranscribableTextarea
                            disabled={isSubmitting}
                            placeholder={translate(
                                'pages.job-architecture-v2.regenerate-dialog.additional-prompt-placeholder',
                            )}
                            rows={8}
                            textareaClassName="!border-none !shadow-none"
                            value={additionalPrompt}
                            onValueChange={setAdditionalPrompt}
                        />
                    </RecessedSurface>
                    <SwitchesWrapper>
                        {areCommentsAvailable ? (
                            <div className="flex w-full flex-col gap-s">
                                <RecessedSurface
                                    active={false}
                                    hoverActive={true}
                                    hoverPocketTone="neutral"
                                    hoverSurfaceShadow="sm"
                                    hoverSurfaceTone="white"
                                    inactiveSurfaceShadow="sm"
                                    inactiveSurfaceTone="white"
                                    placement="outside"
                                    radius="md"
                                    renderMode="overlay"
                                    transition="none"
                                >
                                    {/* biome-ignore lint/a11y/useKeyWithClickEvents: checkbox remains keyboard-accessible; this extends the pointer hit area across the selector surface */}
                                    {/* biome-ignore lint/a11y/noStaticElementInteractions: selector surface delegates pointer activation to the actual checkbox state */}
                                    <div
                                        className={getCommentsOptionCardClassName({
                                            disabled: isSubmitting,
                                            selected: includeComments,
                                        })}
                                        onClick={toggleIncludeComments}
                                    >
                                        <span className="inline-flex shrink-0 items-center gap-s">
                                            <Checkbox
                                                aria-label={translate(
                                                    'pages.job-architecture-v2.regenerate-dialog.regenerate-with-comments',
                                                )}
                                                checked={includeComments}
                                                className="!transition-none data-[state=checked]:!bg-orange-800"
                                                disabled={isSubmitting}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                }}
                                                onCheckedChange={(checked) => {
                                                    updateIncludeComments(checked === true);
                                                }}
                                            />
                                        </span>
                                        <span className="flex min-w-0 flex-1 items-center justify-between gap-s">
                                            <span className={getCommentsOptionLabelClassName(includeComments)}>
                                                {translate(
                                                    'pages.job-architecture-v2.regenerate-dialog.regenerate-with-comments',
                                                )}
                                            </span>
                                            <Badge
                                                className="shrink-0"
                                                variant={getCommentsBadgeVariant(includeComments)}
                                            >
                                                {translate(
                                                    'pages.job-architecture-v2.regenerate-dialog.comment-count',
                                                    {
                                                        count: commentCount,
                                                    },
                                                )}
                                            </Badge>
                                        </span>
                                        <Button
                                            aria-expanded={areCommentPreviewsOpen}
                                            aria-label={translate('common.more')}
                                            className="!text-gray-700"
                                            iconOnly={true}
                                            size="sm"
                                            type="button"
                                            variant="tertiary"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setAreCommentPreviewsOpen((current) => !current);
                                            }}
                                        >
                                            <IconChevronDownSmall
                                                className={getCommentPreviewsToggleIconClassName(
                                                    areCommentPreviewsOpen,
                                                )}
                                            />
                                        </Button>
                                    </div>
                                    {areCommentPreviewsOpen ? (
                                        <div className="relative">
                                            <div className="flex max-h-[168px] w-full flex-col gap-s overflow-auto px-s pb-s">
                                                {comments.map((comment) => (
                                                    <div
                                                        className="min-w-0 rounded-md bg-white-200 p-xl shadow-sm"
                                                        key={comment.id}
                                                    >
                                                        <CommentMessage
                                                            author={comment.author}
                                                            content={comment.content}
                                                            createdAt={comment.createdAt}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                            <div className={commentsPreviewFadeClassName} />
                                        </div>
                                    ) : null}
                                </RecessedSurface>
                                {includeComments ? (
                                    <RecessedSurface
                                        active={false}
                                        hoverActive={true}
                                        hoverPocketTone="neutral"
                                        hoverSurfaceShadow="sm"
                                        hoverSurfaceTone="white"
                                        inactiveSurfaceShadow="sm"
                                        inactiveSurfaceTone="white"
                                        placement="outside"
                                        radius="md"
                                        renderMode="overlay"
                                        transition="none"
                                    >
                                        {/* biome-ignore lint/a11y/useKeyWithClickEvents: checkbox stays keyboard-accessible; this extends the pointer hit area across the row */}
                                        {/* biome-ignore lint/a11y/noStaticElementInteractions: row delegates pointer activation to the actual checkbox state */}
                                        <div
                                            className={getIncludeRepliesRowClassName({
                                                disabled: isSubmitting,
                                                selected: includeReplies,
                                            })}
                                            onClick={toggleIncludeReplies}
                                        >
                                            <Checkbox
                                                aria-label={translate(
                                                    'pages.job-architecture-v2.regenerate-dialog.include-replies',
                                                )}
                                                checked={includeReplies}
                                                className="!transition-none data-[state=checked]:!bg-orange-800"
                                                disabled={isSubmitting}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                }}
                                                onCheckedChange={(checked) => {
                                                    setIncludeReplies(checked === true);
                                                }}
                                            />
                                            <span className={getIncludeRepliesLabelClassName(includeReplies)}>
                                                {translate(
                                                    'pages.job-architecture-v2.regenerate-dialog.include-replies',
                                                )}
                                            </span>
                                        </div>
                                    </RecessedSurface>
                                ) : null}
                            </div>
                        ) : null}
                        <div className="flex w-full flex-col gap-xs">
                            <span className="text-sm font-medium leading-[1.25rem] text-black-700">
                                {translate('pages.job-architecture-v2.regenerate-dialog.apply-target')}
                            </span>
                            <div
                                aria-label={translate('pages.job-architecture-v2.regenerate-dialog.apply-target')}
                                className="grid grid-cols-2 gap-s"
                                role="radiogroup"
                            >
                                {regenerationTargetOptions.map(({ ariaLabelKey, labelKey, value }) => {
                                    const isSelected = regenerationTarget === value;

                                    return (
                                        <Button
                                            aria-checked={isSelected}
                                            aria-label={translate(ariaLabelKey)}
                                            className={getRegenerationTargetButtonClassName(isSelected)}
                                            disabled={isSubmitting}
                                            iconPosition="leading"
                                            isActive={isSelected}
                                            key={value}
                                            role="radio"
                                            type="button"
                                            variant="tertiary"
                                            onClick={() => {
                                                handleRegenerationTargetChange(value);
                                            }}
                                        >
                                            <RegenerationTargetIndicator selected={isSelected} />
                                            {translate(labelKey)}
                                        </Button>
                                    );
                                })}
                            </div>
                        </div>
                    </SwitchesWrapper>
                </div>
            </PromptComposer>
            <AnimatePresence mode="wait">
                {showSkillRedetectionNotification && (
                    <SkillRedetectionNotification
                        onHide={handleHideSkillRedetectionNotification}
                        onRedetectSkills={handleRedetectSkillsFromNotification}
                    />
                )}
            </AnimatePresence>
        </Fragment>
    );
};
